/**
 * The chat column: one width for everything that reads as part of the
 * conversation.
 *
 * The transcript, the docked queued-messages slot, and the composer are
 * rendered by three different components (and, on the agent surface, from two
 * different packages), so the shared measurement has to live somewhere neither
 * of them owns. Without it the composer stretched to the full pane while the
 * messages above it stayed in a 768px column — on a wide viewport the input bar
 * was visibly wider than everything it was replying to.
 *
 * Consumers own their vertical rhythm; this owns width and horizontal gutters,
 * so a message bubble and the composer line up on both edges.
 *
 * The width is a viewer preference, dragged from either edge of the column (see
 * `ChatColumnResizer`). It travels as a CSS custom property rather than as a
 * prop because the three consumers do not share a parent that could thread one:
 * the surface that owns the pane publishes it once with {@link chatColumnStyle}
 * and every column below follows, including mid-drag, when the resizer writes
 * the property straight to that element. Surfaces that publish nothing keep the
 * original 48rem.
 */
import { useCallback } from 'react';
import { useLocalStorageState } from '@reflex/ui/hooks/useWebStorageState';

/** Set by {@link chatColumnStyle}, read by {@link CHAT_COLUMN_CLASS}. */
export const CHAT_COLUMN_WIDTH_VAR = '--chat-column-width';

// Spelled out rather than interpolated from the constant above: Tailwind
// generates utilities by scanning source text, and a class assembled at runtime
// is a class it never sees. The fallback is CHAT_COLUMN_DEFAULT_WIDTH_PX.
export const CHAT_COLUMN_CLASS = 'mx-auto w-full max-w-[var(--chat-column-width,48rem)] px-6';

/** 48rem — the width the column had before it was adjustable. */
export const CHAT_COLUMN_DEFAULT_WIDTH_PX = 768;

/** Narrow enough to park the conversation in a strip, wide enough for a diff. */
export const CHAT_COLUMN_MIN_WIDTH_PX = 480;
export const CHAT_COLUMN_MAX_WIDTH_PX = 1600;

export const CHAT_COLUMN_WIDTH_PREF_KEY = 'chat-column-width';

/**
 * The width a drag may set: the adjustable range, narrowed to the pane when the
 * caller knows it.
 *
 * The pane bound applies to the drag, not to what is stored. The pane is shared
 * with the nav dock and the detail panel, so a column dragged wide with the
 * dock collapsed should come back at that width when the dock closes again,
 * rather than shrinking permanently the first time it is shown in half a
 * window. Nothing has to re-clamp for drawing: the column is `w-full` beside
 * its `max-w`, so CSS already caps it at the pane.
 */
export function clampChatColumnWidth(width: number, paneWidth?: number): number {
  if (!Number.isFinite(width)) return CHAT_COLUMN_DEFAULT_WIDTH_PX;
  const max =
    paneWidth !== undefined && paneWidth > 0
      ? Math.min(CHAT_COLUMN_MAX_WIDTH_PX, Math.max(CHAT_COLUMN_MIN_WIDTH_PX, paneWidth))
      : CHAT_COLUMN_MAX_WIDTH_PX;
  return Math.round(Math.min(max, Math.max(CHAT_COLUMN_MIN_WIDTH_PX, width)));
}

/**
 * The viewer's chat column width, and the only way to set it: the setter
 * clamps, so no caller has to know the range or repeat the rule.
 *
 * Shared through `localStorage` rather than through context, because the
 * composer lives in the host app and the transcript in this plugin and neither
 * can import the other's provider. Pass `paneWidth` where it is known so a drag
 * cannot commit a width the pane could not show.
 */
export function useChatColumnWidth(paneWidth?: number): [number, (width: number) => void] {
  const [stored, setStored] = useLocalStorageState<number>(
    CHAT_COLUMN_WIDTH_PREF_KEY,
    CHAT_COLUMN_DEFAULT_WIDTH_PX,
  );
  const setWidth = useCallback(
    (width: number) => setStored(clampChatColumnWidth(width, paneWidth)),
    [setStored, paneWidth],
  );
  // Clamped on read too: a stored value can predate a range change, or a
  // narrower screen, or a hand edit.
  return [clampChatColumnWidth(stored), setWidth];
}

/** Style for the element that owns the column width for everything beneath it. */
export function chatColumnStyle(width: number): React.CSSProperties {
  return { [CHAT_COLUMN_WIDTH_VAR]: `${width}px` } as React.CSSProperties;
}
